import React, { useContext } from "react";
import styled from "styled-components";
import { ThemeContext } from "grommet";
import { ChatOption, LinkUp } from "grommet-icons";
import { BRAND_GREEN, themed } from "./chatTheme";

const BASE_SHADOW_LIGHT = "0 8px 32px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
const BASE_SHADOW_DARK = "0 8px 32px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.15)";
const GLOW_LIGHT = `0 0 18px rgba(1, 169, 130, 0.25), ${BASE_SHADOW_LIGHT}`;

// $glow: light-mode attention glow, shown only while the idle bar isn't hovered or focused
const Bar = styled.form`
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    box-sizing: border-box;
    padding: 8px 8px 8px 24px;
    border-radius: 999px;
    border: 1px solid ${(props) => (props.$glow
        ? themed("rgba(1, 169, 130, 0.45)", "rgba(255, 255, 255, 0.18)")(props)
        : themed("rgba(255, 255, 255, 0.35)", "rgba(255, 255, 255, 0.18)")(props))};
    /* smoky tint so the white glass lettering stays visible even over white page areas */
    background: linear-gradient(135deg, rgba(20, 20, 20, 0.2), rgba(20, 20, 20, 0.2));
    backdrop-filter: blur(12px) saturate(150%);
    -webkit-backdrop-filter: blur(12px) saturate(150%);
    box-shadow: ${(props) => themed(props.$glow ? GLOW_LIGHT : BASE_SHADOW_LIGHT, BASE_SHADOW_DARK)(props)};
    transition: background 0.25s ease, border-color 0.25s ease, backdrop-filter 0.25s ease, box-shadow 0.25s ease;

    &:hover {
        border-color: ${themed("rgba(255, 255, 255, 0.35)", "rgba(255, 255, 255, 0.18)")};
        box-shadow: ${themed(BASE_SHADOW_LIGHT, BASE_SHADOW_DARK)};
    }

    /* lighter, more see-through glass on focus; stronger blur keeps typed text legible */
    &:focus-within {
        border-color: ${themed("rgba(255, 255, 255, 0.6)", "rgba(255, 255, 255, 0.25)")};
        box-shadow: ${themed(BASE_SHADOW_LIGHT, BASE_SHADOW_DARK)};
        background: ${themed(
            "linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.01))",
            "linear-gradient(135deg, rgba(40, 40, 40, 0.5), rgba(40, 40, 40, 0.35))"
        )};
        backdrop-filter: blur(20px) saturate(160%);
        -webkit-backdrop-filter: blur(20px) saturate(160%);
    }
`;

const Input = styled.input`
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    color: ${themed("#1c1c1c", "#ffffff")};
    /* soft halo keeps typed text readable over busy content behind the glass */
    text-shadow: ${themed("0 0 6px rgba(255, 255, 255, 0.8)", "0 0 6px rgba(0, 0, 0, 0.8)")};
    font-size: 18px;
    font-family: inherit;

    /* idle: frosted, see-through lettering */
    &::placeholder {
        color: rgba(255, 255, 255, 0.6);
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25), 0 0 10px rgba(255, 255, 255, 0.5);
        transition: color 0.25s ease, text-shadow 0.25s ease;
    }

    ${Bar}:focus-within &::placeholder {
        color: ${themed("rgba(0, 0, 0, 0.65)", "rgba(255, 255, 255, 0.75)")};
        text-shadow: ${themed("0 0 6px rgba(255, 255, 255, 0.8)", "0 0 6px rgba(0, 0, 0, 0.8)")};
    }
`;

const SendButton = styled.button`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    cursor: pointer;
    transition: background 0.25s ease, border-color 0.25s ease;

    ${Bar}:focus-within & {
        border-color: rgba(255, 255, 255, 0.4);
        background: ${themed(BRAND_GREEN, "rgba(255, 255, 255, 0.75)")};
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    &:disabled {
        cursor: default;
        opacity: 0.5;
    }
`;

// installed grommet-icons has no sparkle icon
const Sparkle = styled.svg`
    flex: 0 0 auto;
    color: rgba(255, 255, 255, 0.7);
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25)) drop-shadow(0 0 6px rgba(255, 255, 255, 0.5));
    transition: color 0.25s ease, filter 0.25s ease;

    ${Bar}:focus-within & {
        color: ${themed(BRAND_GREEN, "#ffffff")};
        filter: drop-shadow(0 0 4px ${themed("rgba(255, 255, 255, 0.9)", "rgba(0, 0, 0, 0.8)")});
    }
`;

const SparkleIcon = () => (
    <Sparkle width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M9 4.5c.6 3.6 2.4 5.4 6 6-3.6.6-5.4 2.4-6 6-.6-3.6-2.4-5.4-6-6 3.6-.6 5.4-2.4 6-6z" />
        <path d="M18 2c.3 1.7 1.1 2.5 2.8 2.8-1.7.3-2.5 1.1-2.8 2.8-.3-1.7-1.1-2.5-2.8-2.8C16.9 4.5 17.7 3.7 18 2z" />
    </Sparkle>
);

export const ChatInputBar = ({ value, onChange, onSubmit, disabled, autoFocus, onOpenHistory, glow = false }) => {
    const theme = useContext(ThemeContext);
    const iconColor = theme && theme.dark ? "#333333" : "#ffffff";
    // with an empty box and existing history, the send button reopens the chat instead
    const opensHistory = Boolean(onOpenHistory) && !value.trim();
    return (
        <Bar
            $glow={glow}
            onSubmit={(event) => {
                event.preventDefault();
                if (opensHistory) onOpenHistory();
                else onSubmit();
            }}
        >
            <SparkleIcon />
            <Input
                placeholder="Ask anything competitive..."
                aria-label="Ask anything competitive..."
                value={value}
                disabled={disabled}
                autoFocus={autoFocus}
                onChange={(event) => onChange(event.target.value)}
            />
            <SendButton
                type="submit"
                disabled={disabled || (!opensHistory && !value.trim())}
                aria-label={opensHistory ? "Open chat history" : "Send message"}
                title={opensHistory ? "Open chat history" : undefined}
            >
                {opensHistory ? <ChatOption color={iconColor} /> : <LinkUp color={iconColor} />}
            </SendButton>
        </Bar>
    );
};
